import { apiClient } from './client'

// User data management
const USER_DATA_KEY = 'user_data';
const TOKEN_KEY = 'access_token';

export const authService = {
  // Authentication methods
  async login(payload) {
    const response = await apiClient.post('/auth/login', payload);
    console.log('Full login response:', response);
    
    if (response?.data) {
      console.log('Login response data:', response.data);
      
      // Try multiple possible token keys
      const possibleTokens = [
        response.data.access_token,
        response.data.token,
        response.data.accessToken,
        response.data.data?.access_token,
        response.data.data?.token
      ];
      
      const token = possibleTokens.find(t => t);
      console.log('Found token:', !!token, 'Token value:', token);
      
      if (token) {
        localStorage.setItem(TOKEN_KEY, token);
        console.log('Token stored successfully');
      } else {
        console.error('No token found in response');
        // For development/testing - create a dummy token if none provided
        console.warn('Creating dummy token for testing...');
        localStorage.setItem(TOKEN_KEY, 'dummy_token_' + Date.now());
      }
      
      // Store user data including admin status 
      // Extract is_admin from multiple possible locations
      const possibleIsAdmin = [
        response.data.user?.is_admin,
        response.data.data?.user?.is_admin,
        response.data.is_admin,
        response.data.data?.is_admin
      ];
      const isAdmin = possibleIsAdmin.find(val => val === true || val === false);

      const possibleIsSystemAdmin = [
        response.data.user?.is_system_admin,
        response.data.data?.user?.is_system_admin,
        response.data.is_system_admin,
        response.data.data?.is_system_admin
      ];
      const isSystemAdmin = possibleIsSystemAdmin.find(val => val === true || val === false);
      
      const userData = {
        id: response.data.user?.id || response.data.data?.user?.id,
        email: response.data.user?.email || response.data.data?.user?.email,  
        full_name: response.data.user?.full_name || response.data.data?.user?.full_name,
        phone: response.data.user?.phone || response.data.data?.user?.phone || '',
        is_admin: isAdmin !== undefined ? isAdmin : false,
        is_system_admin: isSystemAdmin !== undefined ? isSystemAdmin : false,
        tenant_id: response.data.tenant?.id || response.data.data?.tenant?.id,
        tenant_name: response.data.tenant?.name || response.data.data?.tenant?.name,
        created_at: response.data.user?.created_at || response.data.data?.user?.created_at || new Date().toISOString(),
        avatar_url: response.data.user?.avatar_url || response.data.data?.user?.avatar_url || null,
        permissions: response.data.user?.permissions
          || response.data.data?.user?.permissions
          || response.data.permissions
          || response.data.data?.permissions
          || null,
      };
      
      console.log('User data to store:', userData);
      console.log('is_admin detected:', isAdmin, 'from response');
      localStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
      
      // Store avatar_url separately for easy access
      if (userData.avatar_url) {
        localStorage.setItem('avatar_url', userData.avatar_url);
      }
      
      // Also store tenant info for easy access
      if (userData.tenant_id) {
        localStorage.setItem('tenantId', userData.tenant_id);
      }
      if (userData.tenant_name) {
        localStorage.setItem('tenantName', userData.tenant_name);
      }
      // Store tenant slug if available
      const tenantSlug = response.data.tenant?.slug || response.data.data?.tenant?.slug;
      if (tenantSlug) {
        localStorage.setItem('tenantSlug', tenantSlug);
      }
      
      // Verify storage
      const storedToken = localStorage.getItem(TOKEN_KEY);
      const storedUserData = localStorage.getItem(USER_DATA_KEY);
      console.log('Storage verification:');
      console.log('- stored token:', !!storedToken);
      console.log('- stored user data:', !!storedUserData);
      console.log('- isAuthenticated after storage:', this.isAuthenticated());
    }
    
    return response;
  },
  
  async logout() {
    // Clear all authentication data
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_DATA_KEY);
    localStorage.removeItem('selectedTenant');
    localStorage.removeItem('token');
    localStorage.removeItem('tenantId');
    localStorage.removeItem('username');
    localStorage.removeItem('email');
    localStorage.removeItem('avatar_url');
    
    // Clear any other app-specific data
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('shift_') || key.startsWith('cart_') || key.startsWith('sale_'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    console.log('Logout: All data cleared');
  },
  
  profile() {
    return apiClient.get('/profile')
  },
  
  myTenants() {
    return apiClient.get('/me/tenants')
  },
  
  // User data utilities
  getCurrentUser() {
    const userData = localStorage.getItem(USER_DATA_KEY);
    const parsed = userData ? JSON.parse(userData) : null;
    console.log('getCurrentUser:', parsed);
    return parsed;
  },
  
  isAuthenticated() {
    const token = localStorage.getItem(TOKEN_KEY);
    const userData = this.getCurrentUser();
    const result = !!(token && userData);
    
    console.log('isAuthenticated check:', {
      hasToken: !!token,
      tokenLength: token?.length || 0,
      hasUserData: !!userData,
      result: result
    });
    
    return result;
  },
  
  isAdmin() {
    const userData = this.getCurrentUser();
    const isAdmin = userData?.is_admin === true;
    console.log('isAdmin check:', {
      userData: userData,
      is_admin: userData?.is_admin,
      result: isAdmin
    });
    return isAdmin;
  },
  
  getToken() {
    return localStorage.getItem(TOKEN_KEY);
  },

  /**
   * Check if current user has a specific permission slug.
   * Admins always pass. If permissions not yet stored, allow (graceful degradation).
   * @param {string} slug - e.g. 'pos.cancel_trx'
   */
  isSystemAdmin() {
    const userData = this.getCurrentUser();
    return userData ? userData.is_system_admin === true : false;
  },

  hasPermission(slug) {
    const userData = this.getCurrentUser();
    if (!userData) return false;
    if (userData.is_admin === true) return true;
    const perms = userData.permissions;
    if (!Array.isArray(perms) || perms.length === 0) return false; // strict RBAC: no permissions = no access
    return perms.some(p => (typeof p === 'string' ? p : p?.slug) === slug);
  },

  // Admin verification
  requireAdmin() {
    if (!this.isAdmin()) {
      throw new Error('Admin access required');
    }
    return true;
  }
}
